import { useQuery } from '@apollo/client/react';
import { useEffect } from 'react';

import { useQueryErrorToast } from './useQueryErrorToast';
import { Chef } from '../libs';
import { LIST_CHEFS_QUERY, SUBSCRIBE_TO_CHEF_CHANGES_SUBSCRIPTION } from '../libs/graphQL';

type ListChefsData = { listChefs: { data: Chef[] } };

/** Replaces a chef already in the list, prepends a new one, and drops deleted chefs. */
export const applyChefChange = (chefs: Chef[], chef: Chef): Chef[] => {
  const merged = chefs.some((c) => c.id === chef.id) ? chefs.map((c) => (c.id === chef.id ? chef : c)) : [chef, ...chefs];
  return merged.filter((c) => !c.deletedAt);
};

const useListChefsQuery = (kitchen: string) => {
  const { data, error, loading, refetch, subscribeToMore } = useQuery<ListChefsData>(LIST_CHEFS_QUERY, {
    variables: { kitchen }
  });

  const chefs = data?.listChefs?.data ?? [];

  useQueryErrorToast(error);

  useEffect(
    () =>
      subscribeToMore<{ subscribeToChefChanges: Chef }>({
        document: SUBSCRIBE_TO_CHEF_CHANGES_SUBSCRIPTION,
        variables: { kitchen },
        updateQuery: (previous, { subscriptionData }) => {
          const prev = previous as ListChefsData;
          if (!subscriptionData.data) return prev;
          const prevData = Array.isArray(prev?.listChefs?.data) ? prev.listChefs.data : [];

          return {
            ...prev,
            listChefs: {
              ...prev.listChefs,
              data: applyChefChange(prevData, subscriptionData.data.subscribeToChefChanges)
            }
          };
        }
      }),
    [kitchen, subscribeToMore]
  );

  return {
    chefsLoading: loading,
    chefs,
    refetchChefs: refetch
  };
};

export default useListChefsQuery;
